<template>
  <div class="gps-compare-map">
    <div class="gps-compare-map__legend">
      <span class="legend-item legend-item--before">
        <i class="legend-dot" />
        {{ $t('superviseur.aValider.gpsAvant') }}
      </span>
      <span class="legend-item legend-item--after">
        <i class="legend-dot" />
        {{ $t('superviseur.aValider.gpsApres') }}
      </span>
    </div>
    <div ref="mapContainer" class="gps-compare-map__canvas" />
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import customMarker from '@/assets/marquer.png';

const MAPBOX_TOKEN = 'pk.eyJ1IjoiZGV2b25lc295IiwiYSI6ImNsMTR0c2Y0MDBkdWIzZHF6cjk5OWJ1NnMifQ.cEu2E93WCC2rY74DEjqixg';

function parseGps(gps) {
  if (!gps) {
    return null;
  }
  if (typeof gps === 'string') {
    try {
      return JSON.parse(gps);
    } catch (error) {
      return null;
    }
  }
  return gps;
}

function normalizePoint(point) {
  if (!point) {
    return null;
  }

  if (Array.isArray(point) && point.length >= 2) {
    const lng = Number(point[0]);
    const lat = Number(point[1]);
    if (Number.isFinite(lng) && Number.isFinite(lat)) {
      return [lng, lat];
    }
    return null;
  }

  if (typeof point === 'object') {
    const lat = point.latitude ?? point.lat;
    const lng = point.longitude ?? point.lng ?? point.long;
    if (lat !== null && lat !== undefined && lng !== null && lng !== undefined) {
      const lngNum = Number(lng);
      const latNum = Number(lat);
      if (Number.isFinite(lngNum) && Number.isFinite(latNum)) {
        return [lngNum, latNum];
      }
    }
  }

  return null;
}

function closeRing(coords) {
  if (!coords.length) {
    return coords;
  }
  const first = coords[0];
  const last = coords[coords.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) {
    return coords;
  }
  return [...coords, first];
}

function gpsToRing(gps) {
  const data = parseGps(gps);
  if (!data) {
    return null;
  }

  if (data.type === 'Point' && Array.isArray(data.coordinates) && data.coordinates.length >= 2) {
    const point = normalizePoint(data.coordinates);
    return point ? [point] : null;
  }

  if (data.type === 'Polygon' && Array.isArray(data.coordinates) && data.coordinates.length > 0) {
    const ring = data.coordinates[0]
      .map(point => normalizePoint(point))
      .filter(Boolean);
    return ring.length ? ring : null;
  }

  if (data.type === 'LineString' && Array.isArray(data.coordinates)) {
    const ring = data.coordinates
      .map(point => normalizePoint(point))
      .filter(Boolean);
    return ring.length ? ring : null;
  }

  if (Array.isArray(data) && data.length === 2 && typeof data[0] === 'number' && typeof data[1] === 'number') {
    const point = normalizePoint(data);
    return point ? [point] : null;
  }

  if (Array.isArray(data) && data.length > 0) {
    const ring = data
      .map(point => normalizePoint(point))
      .filter(Boolean);
    return ring.length ? ring : null;
  }

  return null;
}

function ringToFeature(ring, id) {
  if (!ring || !ring.length) {
    return null;
  }

  if (ring.length === 1) {
    return {
      type: 'Feature',
      properties: { id, isPolygon: false },
      geometry: {
        type: 'Point',
        coordinates: ring[0],
      },
    };
  }

  return {
    type: 'Feature',
    properties: { id, isPolygon: true },
    geometry: {
      type: 'Polygon',
      coordinates: [closeRing(ring)],
    },
  };
}

function gpsToFeature(gps, id) {
  return ringToFeature(gpsToRing(gps), id);
}

export default {
  name: 'GpsCompareMap',
  props: {
    gpsBefore: {
      type: [Object, Array, String],
      default: null,
    },
    gpsAfter: {
      type: [Object, Array, String],
      default: null,
    },
  },
  data() {
    return {
      map: null,
      mapLoaded: false,
      markerImageReady: false,
    };
  },
  watch: {
    gpsBefore() {
      this.refreshLayers();
    },
    gpsAfter() {
      this.refreshLayers();
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initMap();
    });
  },
  beforeDestroy() {
    this.destroyMap();
  },
  methods: {
    resizeMap() {
      if (this.map) {
        this.map.resize();
        this.refreshLayers();
      }
    },
    destroyMap() {
      if (this.map) {
        this.map.remove();
        this.map = null;
      }
      this.mapLoaded = false;
      this.markerImageReady = false;
    },
    initMap() {
      if (!this.$refs.mapContainer) {
        return;
      }
      this.destroyMap();
      mapboxgl.accessToken = MAPBOX_TOKEN;
      this.map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: 'mapbox://styles/mapbox/satellite-streets-v12',
        center: [0.824782, 8.619543],
        zoom: 6.4,
      });
      this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');
      this.map.on('load', () => {
        this.mapLoaded = true;
        this.map.loadImage(customMarker, (error, image) => {
          if (!error && !this.map.hasImage('gps-compare-marker')) {
            this.map.addImage('gps-compare-marker', image);
          }
          this.markerImageReady = true;
          this.refreshLayers();
        });
      });
    },
    removeLayerAndSource(layerIds, sourceId) {
      layerIds.forEach((layerId) => {
        if (this.map.getLayer(layerId)) {
          this.map.removeLayer(layerId);
        }
      });
      if (this.map.getSource(sourceId)) {
        this.map.removeSource(sourceId);
      }
    },
    addGeometryLayers(prefix, feature, colors) {
      if (!feature) {
        return;
      }
      const sourceId = `${prefix}-source`;
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [feature],
        },
      });

      if (feature.geometry.type === 'Point') {
        if (this.markerImageReady && this.map.hasImage('gps-compare-marker')) {
          this.map.addLayer({
            id: `${prefix}-point`,
            type: 'symbol',
            source: sourceId,
            layout: {
              'icon-image': 'gps-compare-marker',
              'icon-size': 0.75,
              'icon-allow-overlap': true,
            },
            paint: {
              'icon-color': colors.line,
            },
          });
        } else {
          this.map.addLayer({
            id: `${prefix}-point`,
            type: 'circle',
            source: sourceId,
            paint: {
              'circle-radius': 9,
              'circle-color': colors.fill,
              'circle-stroke-color': colors.line,
              'circle-stroke-width': 2,
            },
          });
        }
        return;
      }

      // Polygone : remplissage + contour
      this.map.addLayer({
        id: `${prefix}-fill`,
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': colors.fill,
          'fill-opacity': colors.fillOpacity != null ? colors.fillOpacity : 0.35,
        },
      });
      this.map.addLayer({
        id: `${prefix}-outline`,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': colors.line,
          'line-width': colors.lineWidth != null ? colors.lineWidth : 3,
          'line-dasharray': colors.dashArray || [1, 0],
        },
      });
    },
    refreshLayers() {
      if (!this.map || !this.mapLoaded || !this.map.isStyleLoaded()) {
        if (this.map && this.mapLoaded) {
          this.map.once('idle', () => this.refreshLayers());
        }
        return;
      }

      this.removeLayerAndSource(
        ['gps-before-point', 'gps-before-fill', 'gps-before-outline'],
        'gps-before-source'
      );
      this.removeLayerAndSource(
        ['gps-after-point', 'gps-after-fill', 'gps-after-outline'],
        'gps-after-source'
      );

      const beforeFeature = gpsToFeature(this.gpsBefore, 'before');
      const afterFeature = gpsToFeature(this.gpsAfter, 'after');

      this.addGeometryLayers('gps-before', beforeFeature, {
        fill: '#f56c6c',
        line: '#c45656',
        fillOpacity: 0.2,
        lineWidth: 2,
        dashArray: [2, 2],
      });
      this.addGeometryLayers('gps-after', afterFeature, {
        fill: '#00ab53',
        line: '#006633',
        fillOpacity: 0.45,
        lineWidth: 4,
      });

      const features = [beforeFeature, afterFeature].filter(Boolean);
      if (!features.length) {
        return;
      }

      const bounds = new mapboxgl.LngLatBounds();
      features.forEach((feature) => {
        if (feature.geometry.type === 'Point') {
          bounds.extend(feature.geometry.coordinates);
        } else if (feature.geometry.type === 'Polygon') {
          feature.geometry.coordinates[0].forEach(coord => bounds.extend(coord));
        }
      });

      if (!bounds.isEmpty()) {
        this.map.fitBounds(bounds, { padding: 56, maxZoom: 18, duration: 0 });
        this.map.resize();
      }
    },
  },
};
</script>

<style scoped>
.gps-compare-map__legend {
  display: flex;
  gap: 20px;
  margin-bottom: 10px;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #606266;
}

.legend-dot {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  display: inline-block;
}

.legend-item--before .legend-dot {
  background: rgba(245, 108, 108, 0.45);
  border: 2px solid #c45656;
}

.legend-item--after .legend-dot {
  background: rgba(0, 171, 83, 0.45);
  border: 2px solid #006633;
}

.gps-compare-map__canvas {
  width: 100%;
  height: 420px;
  border-radius: 4px;
  border: 1px solid #ebeef5;
}
</style>
